import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { useField, useFormikContext } from "formik";
import { pickBy, some } from "lodash";
import { flatten } from "@reside/forms/dist/flatten";
import { styled, theme } from "../../../../theme";
import { Drawer } from "../../../../drawer";
import { Icon } from "../../../../icon";
import { ItemLabel, AlignRight, RemoveButton, } from "../../BlockFieldArray";
import { useBlockComponentContext } from "../../../BlockComponentContext";
/**
 * FieldArray item renderer displaying items in collapsible drawers.
 */
export const CollapsibleItems = ({ id, items, headlineLabel, translationKey, onRemove, children, }) => {
    const { Translated, translate, Markdown } = useBlockComponentContext();
    const { errors, touched, isSubmitting, isValidating, setFieldTouched } = useFormikContext();
    const [, { initialValue = [] }] = useField(id);
    const [openedItemIndex, setOpenedItem] = useState(null);
    // HACK: formik touched is sometimes flattend object and sometimes exploded structure
    // we normalise the array of object by flattening so we handle both cases in the same way later
    const normalisedTouched = flatten(pickBy(touched, (_, key) => key.startsWith(id)));
    const clearTouchedState = () => initialValue.forEach((_, index) => {
        if (!fieldArrayItemHasError(errors, normalisedTouched, id, index)) {
            // We remove touched state from items which had initial error and were fixed.
            setFieldTouched(`${id}.${index}`, false);
        }
    });
    return (_jsx(_Fragment, { children: items.map((item, index) => {
            // Items added by clicking 'Add' are always expanded, only the initialItems are collapsed.
            const isCollapsible = initialValue.some(({ id }) => id === item.id);
            // When item has initial error, we prevent it from collapsing.
            const hasError = fieldArrayItemHasError(errors, normalisedTouched, id, index);
            // Read touched state for current item.
            const itemTouchedState = touched[id]?.[index];
            // HACK: after submit _every_ field is touched, even the internal 'id', we use this to filter false-positive programmatic touch (we care only about user's touches)
            const touchedByFormik = itemTouchedState?.id === true;
            // We keep touched items opened, so they don't collapse after some initial field error was fixed.
            // All the form fields are touched when the form is being submitted, so meanwhile we ignore the touched state when we are validating or submitting.
            const isTouched = Boolean(itemTouchedState) &&
                !isValidating &&
                !isSubmitting &&
                !touchedByFormik;
            const isOpened = isCollapsible
                ? hasError || isTouched || openedItemIndex === index
                : true;
            return (_jsx(DrawerContainer, { hasError: hasError, children: _jsx(Drawer, { isOpened: isOpened, disableAnimation: hasError, collapsible: isCollapsible && !hasError, testId: `${id}-headline-${index}`, onToggle: isOpened => {
                        setOpenedItem(isOpened ? index : null);
                        clearTouchedState();
                    }, header: _jsxs(_Fragment, { children: [_jsx(Title, { children: isCollapsible ? (_jsxs(_Fragment, { children: [_jsx(Icon, { className: "arrow-icon", name: isOpened ? "down" : "right", color: theme.color.gray100, size: 14 }), headlineLabel?.translationKey && (_jsx(Translated, { translationKey: headlineLabel.translationKey, render: ({ translation }) => (_jsx(Markdown, { source: translation, values: {
                                                    answers: items.find(({ id }) => item.id === id),
                                                } })) }))] })) : (_jsxs(ItemLabel, { children: [translate(translationKey), " #", index + 1] })) }), _jsx(AlignRight, { children: _jsx(RemoveButton, { translationKey: translationKey, size: "small", onClick: event => {
                                        event.stopPropagation(); // Prevent toggling the drawer
                                        clearTouchedState();
                                        if (index === openedItemIndex) {
                                            setOpenedItem(null);
                                        }
                                        else if (index < openedItemIndex) {
                                            setOpenedItem(openedItemIndex - 1);
                                        }
                                        onRemove(index);
                                    } }) })] }), children: _jsx("fieldset", { disabled: !isOpened, children: children(index) }) }) }, `${id}${index}`));
        }) }));
};
const DrawerContainer = styled.div `
  .arrow-icon {
    margin-right: 0.6em;
  }

  // We use old-school class because, restyling styled component breaks the collapsible animation
  .drawer-container {
    &:focus {
      box-shadow: 0px 0px 2px
        ${({ theme, hasError }) => hasError ? theme.color.deepOrange100 : theme.color.primary};
    }
  }
`;
const Title = styled.div `
  padding: 0.8em 0;
`;
const fieldArrayItemHasError = (errors, touched, id, itemIndex) => some(pickBy(errors, (_, key) => key.startsWith(`${id}.${itemIndex}`)), (_, key) => touched[key]);
